import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface PasswordInputProps {
  password: string;
  onChange: (value: string) => void;
  mode?: "login" | "register";
}

export function PasswordInput({ password, onChange, mode = "login" }: PasswordInputProps) {
  const [showPassword, setShowPassword] = useState(false);

  const validatePassword = (pass: string) => {
    const hasMinLength = pass.length >= 8;
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasLowerCase = /[a-z]/.test(pass);
    const hasNumbers = /\d/.test(pass);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pass);
    
    const strength = [hasMinLength, hasUpperCase, hasLowerCase, hasNumbers, hasSpecialChar].filter(Boolean).length;
    
    return {
      isStrong: strength === 5,
      strength: strength * 20
    };
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const passwordValidation = validatePassword(password);

  return (
    <div className="space-y-2">
      <Label htmlFor="password">Password</Label>
      <div className="relative">
        <Input
          id="password"
          type={showPassword ? "text" : "password"}
          placeholder="Enter your password"
          value={password}
          onChange={(e) => onChange(e.target.value)}
          required
        />
        <button
          type="button"
          onClick={togglePasswordVisibility}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>
      {mode === "register" && password && (
        <div className="mt-2 space-y-2">
          <div className="h-1 w-full bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ${
                passwordValidation.strength <= 40 ? 'bg-red-500' : 
                passwordValidation.strength <= 80 ? 'bg-yellow-500' : 
                'bg-green-500'
              }`}
              style={{ width: `${passwordValidation.strength}%` }}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            Password must be at least 8 characters long and contain uppercase, lowercase, numbers and special characters
          </p>
        </div>
      )}
    </div>
  );
}